<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <v-flex class="pa-3">
      <div class="mb-3">
        <h3
          class="title"
          :class="{'mb-1': !!itemDefaultSetting.notes}"
        >
          {{ itemDefaultSetting.label }}
        </h3>
        {{ itemDefaultSetting.notes ? itemDefaultSetting.notes : '' }}
      </div>

      <template
        v-if="mappedValue"
      >
        <template
          v-for="(option, o1) in itemDefaultSetting.options"
        >
          <!-- TODO: move input type selection to a re-useable component so we don't have duplicate here -->
          <template
            v-if="option.options && option.options.length"
          >
            <v-combobox
              v-if="!isMultiSubType(option) && option.combobox"
              :key="o1"
              v-model="itemMap.value[option.value]"
              autocomplete="off"
              :label="option.name"
              :items="option.options"
              item-text="name"
              :clearable="!!itemDefaultSetting.allowNull || itemDefaultSetting.allowEmpty"
              placeholder="Select Option"
              :hint="option.hint"
              :persistent-hint="!!option.hint"
              :error-messages="getPathErrors(extJsonPath('value'), option.value)"
              :disabled="isFormDisabled"
              @input="clearPathErrors(extJsonPath('value'), option.value)"
              @click:clear="$nextTick(() => itemMap.value[option.value] = null)"
            />
            <v-autocomplete
              v-else
              :key="o1"
              v-model="itemMap.value[option.value]"
              :multiple="isMultiSubType(option)"
              :label="option.name"
              :items="option.options"
              item-text="name"
              :clearable="!!itemDefaultSetting.allowNull || itemDefaultSetting.allowEmpty"
              placeholder="Select Option"
              :hint="option.hint"
              :persistent-hint="!!option.hint"
              :error-messages="getPathErrors(extJsonPath('value'), option.value)"
              :disabled="isFormDisabled"
              @input="clearPathErrors(extJsonPath('value'), option.value)"
              @click:clear="$nextTick(() => {if (!isMultiSubType(option)) itemMap.value[option.value] = null})"
            />
          </template>

          <markup-editor
            v-else-if="option.valueType === 'HTML'"
            :key="o1"
            v-model="itemMap.value[option.value]"
            :label="option.name"
            :error-messages="getPathErrors(extJsonPath('value'), option.value)"
            :hint="option.hint"
            :persistent-hint="!!option.hint"
            :disabled="isFormDisabled"
          />

          <v-textarea
            v-else-if="option.valueType === 'TEXT'"
            :key="o1"
            v-model="itemMap.value[option.value]"
            autocomplete="off"
            :label="option.name"
            :auto-grow="true"
            :rows="1"
            :hint="option.hint"
            :persistent-hint="!!option.hint"
            :error-messages="getPathErrors(extJsonPath('value'), option.value)"
            :disabled="isFormDisabled"
            :type="getSubInputType(option)"
            @input="clearPathErrors(extJsonPath('value'), option.value)"
          />

          <color-picker
            v-else-if="option.valueType && option.valueType.startsWith('HTML_RGB')"
            :key="o1"
            v-model.trim="itemMap.value[option.value]"
            :label="option.name"
            :no-alpha="option.valueType !== 'HTML_RGBA'"
            :default-color="option.defaultValue || '#FFFFFFFF'"
            :hint="option.hint"
            :persistent-hint="!!option.hint"
            :error-messages="getPathErrors(extJsonPath('value'), option.value)"
            :disabled="isFormDisabled"
            @input="clearPathErrors(extJsonPath('value'), option.value)"
          />

          <v-text-field
            v-else
            :key="o1"
            v-model="itemMap.value[option.value]"
            :label="option.name"
            :hint="option.hint"
            :persistent-hint="!!option.hint"
            :error-messages="getPathErrors(extJsonPath('value'), option.value)"
            :disabled="isFormDisabled"
            :type="getSubInputType(option)"
            @input="clearPathErrors(extJsonPath('value'), option.value)"
          />
        </template>
      </template>

      <!-- TODO: move input type selection to a re-useable component so we don't have duplicate here -->
      <template
        v-else-if="itemDefaultSetting.options && itemDefaultSetting.options.length"
      >
        <v-combobox
          v-if="!isMultiType(itemData) && itemDefaultSetting.combobox"
          v-model="itemData.value"
          label="Value"
          :items="itemDefaultSetting.options"
          autocomplete="off"
          item-text="name"
          :clearable="!!itemDefaultSetting.allowNull || itemDefaultSetting.allowEmpty"
          placeholder="Select Option"
          :hint="itemDefaultSetting.hint"
          :persistent-hint="!!itemDefaultSetting.hint"
          :error-messages="getPathErrors(jsonPath, 'value')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'value')"
          @click:clear="$nextTick(() => itemMap.value = null)"
        />
        <v-autocomplete
          v-else
          v-model="itemData.value"
          label="Value"
          :multiple="isMultiType(itemData)"
          :items="itemDefaultSetting.options"
          item-text="name"
          :clearable="!!itemDefaultSetting.allowNull || itemDefaultSetting.allowEmpty"
          placeholder="Select Option"
          :hint="itemDefaultSetting.hint"
          :persistent-hint="!!itemDefaultSetting.hint"
          :error-messages="getPathErrors(jsonPath, 'value')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'value')"
          @click:clear="$nextTick(() => {if (!isMultiType(itemData)) itemMap.value = null})"
        />
      </template>

      <markup-editor
        v-else-if="itemDefaultSetting.valueType === 'HTML'"
        v-model="itemData.value"
        :error-messages="getPathErrors(jsonPath, 'value')"
        :hint="itemDefaultSetting.hint"
        :persistent-hint="!!itemDefaultSetting.hint"
        :disabled="isFormDisabled"
      />

      <v-textarea
        v-else-if="itemDefaultSetting.valueType === 'TEXT'"
        v-model.trim="itemData.value"
        label="Value"
        :auto-grow="true"
        :hint="itemDefaultSetting.hint"
        :persistent-hint="!!itemDefaultSetting.hint"
        :error-messages="getPathErrors(jsonPath, 'value')"
        :disabled="isFormDisabled"
        :type="getInputType(itemData)"
        @input="clearPathErrors(jsonPath, 'value')"
      />

      <color-picker
        v-else-if="itemDefaultSetting.valueType && itemDefaultSetting.valueType.startsWith('HTML_RGB')"
        v-model.trim="itemData.value"
        label="Value"
        :no-alpha="itemDefaultSetting.valueType !== 'HTML_RGBA'"
        :default-color="itemDefaultSetting.value || '#FFFFFFFF'"
        :hint="itemDefaultSetting.hint"
        :persistent-hint="!!itemDefaultSetting.hint"
        :error-messages="getPathErrors(jsonPath, 'value')"
        :disabled="isFormDisabled"
        @input="clearPathErrors(jsonPath, 'value')"
      />

      <v-text-field
        v-else
        v-model.trim="itemData.value"
        autocomplete="off"
        label="Value"
        :hint="itemDefaultSetting.hint"
        :persistent-hint="!!itemDefaultSetting.hint"
        :error-messages="getPathErrors(jsonPath, 'value')"
        :disabled="isFormDisabled"
        :type="getInputType(itemData)"
        @input="clearPathErrors(jsonPath, 'value')"
      />
    </v-flex>

    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import itemEdit from '../../mixins/itemEditor/itemEdit'
import clubSettingsCommon from './mixins/clubSettingsCommon'
import MarkupEditor from '../MarkupEditor'
import ColorPicker from '../ColorPicker'
import {requestClubSettingsRefresh} from '../../store/actions'

var itemDefault = {
  name: null,
  value: null,
  id: null,
  legacy: null,
  recordLockKey: null,
  recordLockExpiration: null
}

export default {
  name: 'ClubSettingsItem',
  components: {MarkupEditor, ColorPicker},
  mixins: [itemEdit, clubSettingsCommon],
  data: function () {
    return {
      type: 'Club Setting',
      types: 'Club Settings',
      groupLabelVal: null,
      apiRecordCommand: 'clubSetting',
      itemDefault: itemDefault,
      mappedValue: false,
      itemMap: {value: {}},
      showPassMap: new WeakMap()
    }
  },
  computed: {
    selectedItems () {
      return this.items.filter(i => !this.groupLabel || this.groupLabel === i.groupLabel)
    },
    isDeleteHidden () {
      return true
    },
    useItemAuditHistory () {
      return !this.itemData.legacy
    },
    itemDefaultSetting () {
      return this.settingDefaultsMap[this.itemData.name] || {}
    }
  },
  methods: {
    isMultiType (item) {
      const defaultSetting = this.getDefaultSetting(item.name)
      return (defaultSetting.valueType || '').startsWith('MULTI_')
    },
    isMultiSubType (option) {
      return ((option || {}).valueType || '').startsWith('MULTI_')
    },
    getSubInputType (option) {
      switch (option.valueType) {
      case 'DOUBLE':
      case 'FLOAT':
      case 'INTEGER':
      case 'LONG':
        return 'number'
      case 'EMAIL':
        return 'email'
      case 'PHONE':
        return 'tel'
      case 'URL':
        return 'url'
      // case 'PASSWORD':
      //   if (!this.showPassword(option)) return 'password'
      // // eslint-disable-next-line no-fallthrough
      default:
        return 'text'
      }
    },
    setShowPassword (obj) {
      this.showPassMap.set(obj, true)
    },
    showPassword (obj) {
      return !! this.showPassMap.set(obj)
    },
    getInputType (item) {
      const defaultSetting = this.getDefaultSetting(item.name)
      switch (defaultSetting.valueType) {
      case 'DOUBLE':
      case 'FLOAT':
      case 'INTEGER':
      case 'LONG':
        return 'number'
      case 'EMAIL':
        return 'email'
      case 'PHONE':
        return 'tel'
      case 'URL':
        return 'url'
      // case 'PASSWORD':
      //   if (!this.showPassword(item)) return 'password'
      // // eslint-disable-next-line no-fallthrough
      default:
        return 'text'
      }
    },
    itemResourceCommands () {
      return this.clubSettingDefaultsCommand()
    },
    itemCommandFilter (command) {
      command.resultFilter = data => {
        return data
      }
      command.params.name = command.params.id // Name should have been passed in ID
      delete command.params.id // We don't have an actual ID
      command.params.include_fields = ['legacy'] // Include legacy settings from club5
      command.resultFilter = r => {
        const defaultSetting = this.buildSettingDefaultsMap()[r.name] || {}
        this.mappedValue = (defaultSetting.valueType || '').startsWith('MAP_') && defaultSetting.options && defaultSetting.options.length
        if (this.mappedValue) {
          // Map of settings.  Make sure all defaults are set.
          var itemMapValue
          try {
            // Convert json map value to object
            itemMapValue = JSON.parse(r.value)
          } catch (e) {
            itemMapValue = {}
          }
          const itemValue = typeof itemMapValue === 'object' ? itemMapValue : {}
          defaultSetting.options.forEach(o => {
            if (itemValue[o.value] === undefined) {
              itemValue[o.value] = o.hasDefault ? o.defaultValue : defaultSetting.value
            }
          })
          this.$set(this.itemMap, 'value', itemValue)
        }
        return r
      }
      return command
    },
    beforeSaveItem (saveItemCallback) {
      if (this.mappedValue) {
        // Convert object back to json value map
        this.itemData.value = JSON.stringify(this.itemMap.value)
      }
      if (JSON.stringify(this.itemData).length > 1000000) {
        this.errors = []
        setTimeout(() => {
          this.errors = ['Message is too large.  Try reducing the size of any images or attachments.']
        }, 0)
        return
      }
      saveItemCallback()
    },
    afterSave () {
      // We changed a club setting
      // Signal that we need to re-load our local club settings
      requestClubSettingsRefresh(this.$store, true)
    }
  }
}
</script>
<style>
  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="x-large"]::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="x-large"]::before {
    content: 'X-Large';
    font-size: 20px;
  }
  .ft-settings-editor .tabs__items {
    overflow: visible;
  }
  .ft-settings-editor .tabs__items .ql-tooltip {
    z-index: 90000;
  }

</style>
